/**
 * Starting points, not destinations.
 *
 * A preset here is an ordinary recipe someone could have dialled by hand — the
 * same controls, the same bounds, visible in the panels the moment it is
 * applied. Nothing is hidden inside a preset that the sliders cannot show and
 * undo.
 *
 * Two facts about the render order shape these numbers (see `MAIN_FRAGMENT`):
 *  - the colour mixer runs *before* the saturation control, which is what lets
 *    the black-and-white preset use per-band luminance as a channel mixer;
 *  - colour grading also runs before saturation, so a toned monochrome is not
 *    reachable by pairing the grading wheels with saturation at -100.
 */

import {
  type CurveChannel,
  type CurvePoint,
  type GradingWheel,
  type GradingZone,
  type HslAdjustment,
  type HslBand,
  type Recipe,
  HSL_BANDS,
  defaultRecipe,
} from '@lumos/core';

export interface Preset {
  id: string;
  name: string;
  /**
   * The qualifier after the name, carried as its own field so the panel can set
   * it in lighter weight. It used to be punctuation inside `name`, which meant
   * the rail had to split the string back apart to render it.
   */
  note?: string;
  /** What it does and what it costs, in the words a photographer would use. */
  description: string;
  patch: Partial<Recipe>;
}

// ------------------------------------------------------------------ builders --

function bands(
  values: Partial<Record<HslBand, Partial<HslAdjustment>>>,
): Record<HslBand, HslAdjustment> {
  return Object.fromEntries(
    HSL_BANDS.map((band) => [band, { hue: 0, saturation: 0, luminance: 0, ...values[band] }]),
  ) as Record<HslBand, HslAdjustment>;
}

function identity(): CurvePoint[] {
  return [
    { x: 0, y: 0 },
    { x: 1, y: 1 },
  ];
}

function curves(
  values: Partial<Record<CurveChannel, CurvePoint[]>>,
): Record<CurveChannel, CurvePoint[]> {
  return {
    rgb: values.rgb ?? identity(),
    red: values.red ?? identity(),
    green: values.green ?? identity(),
    blue: values.blue ?? identity(),
  };
}

type GradingPatch = Partial<Record<GradingZone, Partial<GradingWheel>>> & {
  blending?: number;
  balance?: number;
};

function grading(values: GradingPatch): Recipe['grading'] {
  const wheel = (zone: GradingZone): GradingWheel => ({
    hue: 0,
    saturation: 0,
    luminance: 0,
    ...values[zone],
  });
  return {
    shadows: wheel('shadows'),
    midtones: wheel('midtones'),
    highlights: wheel('highlights'),
    global: wheel('global'),
    blending: values.blending ?? 50,
    balance: values.balance ?? 0,
  };
}

// ------------------------------------------------------------------- presets --

export const PRESETS: readonly Preset[] = [
  {
    id: 'neutral-base',
    name: 'Neutral base',
    description:
      'Every look control back to zero, with modest capture sharpening on edges. An honest starting point for judging a file.',
    patch: {
      sharpenAmount: 30,
      sharpenRadius: 1,
      sharpenDetail: 25,
      sharpenMasking: 20,
      curves: curves({}),
      hsl: bands({}),
      grading: grading({}),
    },
  },

  {
    id: 'portrait-soft',
    name: 'Portrait',
    note: 'soft',
    description:
      'Highlights pulled back, shadows opened, sharpening kept off smooth skin. Warm highlights and cool shadows keep a face separated from its background.',
    patch: {
      contrast: -6,
      highlights: -20,
      shadows: 16,
      whites: 6,
      blacks: -5,
      texture: -10,
      clarity: -6,
      vibrance: 10,
      saturation: -3,
      curves: curves({
        rgb: [
          { x: 0, y: 0.008 },
          { x: 0.25, y: 0.252 },
          { x: 0.75, y: 0.775 },
          { x: 1, y: 1 },
        ],
      }),
      hsl: bands({
        red: { saturation: -4 },
        orange: { saturation: -3, luminance: 8 },
        yellow: { luminance: 4 },
      }),
      grading: grading({
        shadows: { hue: 214, saturation: 5 },
        highlights: { hue: 38, saturation: 7 },
        blending: 60,
      }),
      sharpenAmount: 35,
      sharpenRadius: 0.9,
      sharpenDetail: 18,
      sharpenMasking: 55,
      vignetteAmount: -6,
      vignetteMidpoint: 60,
      vignetteFeather: 70,
    },
  },

  {
    id: 'editorial-contrast',
    name: 'Editorial',
    note: 'contrast',
    description:
      'Deep blacks, a firm S-curve and cool shadows. Check it on faces: this much clarity hardens skin and can crush a low-key frame.',
    patch: {
      contrast: 20,
      highlights: -32,
      shadows: -10,
      whites: 14,
      blacks: -22,
      texture: 10,
      clarity: 15,
      dehaze: 5,
      vibrance: 8,
      saturation: -8,
      curves: curves({
        rgb: [
          { x: 0, y: 0 },
          { x: 0.22, y: 0.17 },
          { x: 0.5, y: 0.5 },
          { x: 0.78, y: 0.845 },
          { x: 1, y: 1 },
        ],
      }),
      hsl: bands({
        orange: { saturation: -6, luminance: 4 },
        aqua: { saturation: -8 },
        blue: { saturation: 8, luminance: -10 },
      }),
      grading: grading({
        shadows: { hue: 218, saturation: 12, luminance: -4 },
        highlights: { hue: 36, saturation: 7 },
        blending: 45,
        balance: 5,
      }),
      sharpenAmount: 55,
      sharpenRadius: 1,
      sharpenDetail: 30,
      sharpenMasking: 35,
      vignetteAmount: -14,
      vignetteMidpoint: 55,
      vignetteFeather: 60,
    },
  },

  {
    id: 'warm-film',
    name: 'Warm film',
    description:
      'Lifted blacks, warm highlights, cool shadows and a little grain, like a scanned colour negative. The lifted toe will not hold a true black.',
    patch: {
      exposure: 0.1,
      contrast: -8,
      highlights: -24,
      shadows: 18,
      whites: -6,
      blacks: 12,
      texture: 4,
      clarity: -5,
      vibrance: 12,
      saturation: -10,
      curves: curves({
        rgb: [
          { x: 0, y: 0.022 },
          { x: 0.25, y: 0.245 },
          { x: 0.75, y: 0.775 },
          { x: 1, y: 0.985 },
        ],
        red: [
          { x: 0, y: 0.03 },
          { x: 0.5, y: 0.52 },
          { x: 1, y: 1 },
        ],
        green: [
          { x: 0, y: 0.012 },
          { x: 0.5, y: 0.5 },
          { x: 1, y: 0.99 },
        ],
        blue: [
          { x: 0, y: 0.062 },
          { x: 0.5, y: 0.485 },
          { x: 1, y: 0.94 },
        ],
      }),
      hsl: bands({
        yellow: { hue: -10, saturation: 6 },
        green: { hue: 12, saturation: -16, luminance: -4 },
        aqua: { saturation: -12 },
        blue: { saturation: -6, luminance: -8 },
      }),
      grading: grading({
        shadows: { hue: 206, saturation: 10 },
        highlights: { hue: 42, saturation: 14 },
        blending: 55,
        balance: 8,
      }),
      sharpenAmount: 20,
      sharpenRadius: 1.2,
      sharpenDetail: 15,
      sharpenMasking: 25,
      grainAmount: 18,
      grainSize: 32,
      vignetteAmount: -10,
      vignetteMidpoint: 50,
      vignetteFeather: 65,
    },
  },

  {
    id: 'black-and-white',
    name: 'Black & white',
    description:
      'Monochrome through the colour mixer. Reds lift so skin separates, blues drop so a sky keeps its weight. The colour is still in the file.',
    patch: {
      saturation: -100,
      contrast: 12,
      highlights: -18,
      shadows: 12,
      whites: 8,
      blacks: -14,
      texture: 8,
      clarity: 8,
      curves: curves({
        rgb: [
          { x: 0, y: 0 },
          { x: 0.25, y: 0.225 },
          { x: 0.75, y: 0.79 },
          { x: 1, y: 1 },
        ],
      }),
      hsl: bands({
        red: { luminance: 20 },
        orange: { luminance: 26 },
        yellow: { luminance: 12 },
        green: { luminance: -6 },
        aqua: { luminance: -18 },
        blue: { luminance: -26 },
        purple: { luminance: -10 },
        magenta: { luminance: 6 },
      }),
      grading: grading({}),
      sharpenAmount: 45,
      sharpenRadius: 1,
      sharpenDetail: 25,
      sharpenMasking: 40,
      grainAmount: 12,
      grainSize: 28,
    },
  },

  {
    id: 'landscape-clear',
    name: 'Landscape',
    note: 'clear',
    description:
      'Recovers a bright sky, opens shadow detail and adds mid-radius punch. This much dehaze can bruise a clean sky, so check the blue channel.',
    patch: {
      contrast: 14,
      highlights: -36,
      shadows: 24,
      whites: 12,
      blacks: -10,
      texture: 16,
      clarity: 12,
      dehaze: 14,
      vibrance: 20,
      saturation: -4,
      curves: curves({
        rgb: [
          { x: 0, y: 0 },
          { x: 0.25, y: 0.228 },
          { x: 0.75, y: 0.788 },
          { x: 1, y: 1 },
        ],
      }),
      hsl: bands({
        yellow: { hue: -6, luminance: 6 },
        green: { hue: -10, saturation: 8, luminance: 4 },
        aqua: { hue: 14, saturation: 10 },
        blue: { saturation: 12, luminance: -18 },
      }),
      grading: grading({
        shadows: { hue: 210, saturation: 6 },
        highlights: { hue: 45, saturation: 5 },
        blending: 55,
      }),
      sharpenAmount: 60,
      sharpenRadius: 1.1,
      sharpenDetail: 35,
      sharpenMasking: 20,
      vignetteAmount: -8,
      vignetteMidpoint: 55,
      vignetteFeather: 60,
    },
  },
];

/**
 * Applies a preset to a recipe.
 *
 * A preset replaces the look wholesale rather than layering onto whatever was
 * there — trying three presets in a row should show you three looks, not an
 * accumulation of all of them. What survives belongs to the photograph rather
 * than to the look: white balance (a property of the light the frame was made
 * in), the crop and orientation, any local masks already painted, and noise
 * reduction, which follows the capture's ISO and not anyone's taste.
 */
export function applyPreset(recipe: Recipe, preset: Preset): Recipe {
  const next: Recipe = { ...defaultRecipe(), ...structuredClone(preset.patch) };

  next.temperature = recipe.temperature;
  next.tint = recipe.tint;
  next.crop = recipe.crop ? { ...recipe.crop } : null;
  next.rotate = recipe.rotate;
  next.orientation = recipe.orientation;
  next.flipHorizontal = recipe.flipHorizontal;
  next.flipVertical = recipe.flipVertical;
  next.masks = structuredClone(recipe.masks);
  next.noiseLuminance = recipe.noiseLuminance;
  next.noiseLuminanceDetail = recipe.noiseLuminanceDetail;
  next.noiseColor = recipe.noiseColor;

  return next;
}
